import { z } from "zod";
import { IsoDateTime, Uuid } from "./common";

const Name = z.string().trim().min(1).max(200);
const Slug = z.string().trim().regex(/^[a-z][a-z0-9_.-]{0,63}$/);
const Html = z.string().max(200_000);
const Body = z.string().max(20_000);
const Subject = z.string().trim().max(300);
const HttpsUrl = z.string().trim().max(2000).regex(/^https:\/\//);
const Country = z.string().trim().regex(/^[A-Za-z]{2}$/);
const VariableName = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/);

export const MESSAGING_CHANNELS = ["email", "sms", "viber", "whatsapp"] as const;
export const MessagingChannel = z.enum(MESSAGING_CHANNELS);
export type MessagingChannel = z.infer<typeof MessagingChannel>;

export const MESSAGING_LANGUAGES = ["ro", "bg", "hu", "en"] as const;
export const MessagingLanguage = z.enum(MESSAGING_LANGUAGES);
export type MessagingLanguage = z.infer<typeof MessagingLanguage>;

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export const TEMPLATE_STATUSES = ["draft", "active", "archived"] as const;
export const TEMPLATE_PURPOSES = ["campaign", "journey", "auto_reply", "sequence"] as const;

export const TemplateMatch = z
  .object({
    forms: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
    angles: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
    sources: z.array(z.string().trim().min(1).max(100)).max(50).default([]),
    purposes: z.array(z.enum(TEMPLATE_PURPOSES)).max(4).default([]),
  })
  .strict();
export type TemplateMatch = z.infer<typeof TemplateMatch>;

const TemplateBody = {
  name: Name,
  channel: MessagingChannel,
  language: MessagingLanguage,
  subject: Subject.nullable().optional(),
  html: Html.nullable().optional(),
  text: Body.nullable().optional(),
  whatsappTemplate: z.string().trim().max(200).nullable().optional(),
  /** Every placeholder that is not a contact field has to be declared here, or saving refuses it. */
  variables: z.array(VariableName).max(50).optional(),
  fallbacks: z.record(VariableName, z.string().max(500)).optional(),
  match: TemplateMatch.optional(),
};

export const TemplateCreateInput = z.object(TemplateBody).strict();
export type TemplateCreateInput = z.infer<typeof TemplateCreateInput>;

export const TemplateUpdateInput = z.object({ ...TemplateBody, name: Name.optional(), channel: MessagingChannel.optional(), language: MessagingLanguage.optional(), status: z.enum(TEMPLATE_STATUSES).optional() }).strict();
export type TemplateUpdateInput = z.infer<typeof TemplateUpdateInput>;

export const TemplatePreviewInput = z.object({ values: z.record(VariableName, z.string().max(2000)).default({}), contactId: Uuid.optional() }).strict();
export type TemplatePreviewInput = z.infer<typeof TemplatePreviewInput>;

export const TemplateTestSendInput = z.object({ to: z.string().trim().min(3).max(320), values: z.record(VariableName, z.string().max(2000)).default({}) }).strict();
export type TemplateTestSendInput = z.infer<typeof TemplateTestSendInput>;

export const TemplateGenerateInput = z
  .object({
    channel: MessagingChannel,
    language: MessagingLanguage,
    brief: z.string().trim().min(3).max(2000),
    name: Name.optional(),
    match: TemplateMatch.optional(),
  })
  .strict();
export type TemplateGenerateInput = z.infer<typeof TemplateGenerateInput>;

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATUSES = ["draft", "scheduled", "sending", "ab_waiting", "waiting", "sent", "rehearsed", "paused", "blocked", "failed", "cancelled"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];
export const AB_WINNER_RULES = ["open_rate", "click_rate"] as const;
export const CAMPAIGN_RECURRENCES = ["daily", "weekly", "monthly"] as const;

const CampaignBody = {
  name: Name,
  channel: MessagingChannel,
  segmentId: Uuid.nullable().optional(),
  templateId: Uuid.nullable().optional(),
  language: MessagingLanguage.nullable().optional(),
  subject: Subject.nullable().optional(),
  /** Variant B of the subject. Set: an A/B test runs on `abTestPercent` of the audience. */
  subjectB: Subject.nullable().optional(),
  html: Html.nullable().optional(),
  text: Body.nullable().optional(),
  whatsappTemplate: z.string().trim().max(200).nullable().optional(),
  values: z.record(VariableName, z.string().max(2000)).optional(),
  abTestPercent: z.number().int().min(0).max(100).optional(),
  abWinnerRule: z.enum(AB_WINNER_RULES).optional(),
  abWaitHours: z.number().int().min(1).max(168).optional(),
  recurrence: z.enum(CAMPAIGN_RECURRENCES).nullable().optional(),
  maxPerDay: z.number().int().min(1).max(1_000_000).nullable().optional(),
};

export const CampaignCreateInput = z.object(CampaignBody).strict();
export type CampaignCreateInput = z.infer<typeof CampaignCreateInput>;

export const CampaignUpdateInput = z.object({ ...CampaignBody, name: Name.optional(), channel: MessagingChannel.optional() }).strict();
export type CampaignUpdateInput = z.infer<typeof CampaignUpdateInput>;

export const CampaignScheduleInput = z.object({ at: z.string().datetime().optional(), rehearsal: z.boolean().default(false) }).strict();
export type CampaignScheduleInput = z.infer<typeof CampaignScheduleInput>;

// ---------------------------------------------------------------------------
// Journeys
// ---------------------------------------------------------------------------

export const JOURNEY_STATUSES = ["draft", "active", "paused", "archived"] as const;
export const JOURNEY_TRIGGERS = ["event", "segment", "manual"] as const;

const StepCopy = z
  .object({
    subject: Subject.nullable().optional(),
    html: Html.nullable().optional(),
    text: Body.nullable().optional(),
    whatsappTemplate: z.string().trim().max(200).nullable().optional(),
  })
  .strict();

export const JourneyCondition = z.discriminatedUnion("type", [
  z.object({ type: z.literal("has_tag"), tag: z.string().min(1).max(100) }).strict(),
  z.object({ type: z.literal("missing_tag"), tag: z.string().min(1).max(100) }).strict(),
  z.object({ type: z.literal("country_in"), countries: z.array(Country).min(1).max(50) }).strict(),
  z.object({ type: z.literal("language_in"), languages: z.array(MessagingLanguage).min(1).max(4) }).strict(),
  /** The last message this enrollment sent reached that point. */
  z.object({ type: z.literal("opened_last") }).strict(),
  z.object({ type: z.literal("clicked_last") }).strict(),
  /** A value the host fed back from an action, or the trigger event's data. Never available in a regulated vertical. */
  z.object({ type: z.literal("state_equals"), key: z.string().min(1).max(64), value: z.string().max(200) }).strict(),
]);
export type JourneyCondition = z.infer<typeof JourneyCondition>;

export const JourneyStep = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("wait"), hours: z.number().int().min(1).max(24 * 365) }).strict(),
  z.object({ kind: z.literal("condition"), test: JourneyCondition, ifFalse: z.enum(["exit", "skip_next"]).default("exit") }).strict(),
  z.object({ kind: z.literal("tag"), add: z.array(z.string().min(1).max(100)).max(20).default([]), remove: z.array(z.string().min(1).max(100)).max(20).default([]) }).strict(),
  z
    .object({
      kind: z.literal("send"),
      channel: MessagingChannel,
      templateId: Uuid.optional(),
      /** Copy per language, when no template is named. */
      copy: z.partialRecord(MessagingLanguage, StepCopy).optional(),
      values: z.record(VariableName, z.string().max(2000)).optional(),
    })
    .strict(),
  /** Asks the host to do something, over the signed webhook, and waits for its answer or for the timeout. */
  z.object({ kind: z.literal("action"), action: Slug, params: z.record(z.string(), z.unknown()).default({}), timeoutHours: z.number().int().min(1).max(24 * 30).default(48), onFailure: z.enum(["continue", "exit"]).default("continue") }).strict(),
  /** Tells the host a contact reached this step, over the signed webhook. Nothing is waited for. */
  z.object({ kind: z.literal("webhook"), event: Slug }).strict(),
  z.object({ kind: z.literal("exit"), reason: z.string().max(100).default("step") }).strict(),
]);
export type JourneyStep = z.infer<typeof JourneyStep>;
export const JourneySteps = z.array(JourneyStep).min(1).max(50);

export const JourneyTriggerConditions = z.object({ countries: z.array(Country).max(50).optional(), languages: z.array(MessagingLanguage).max(4).optional(), tags: z.array(z.string().min(1).max(100)).max(20).optional() }).strict();
export type JourneyTriggerConditions = z.infer<typeof JourneyTriggerConditions>;

const JourneyBody = {
  name: Name,
  channel: MessagingChannel,
  triggerKind: z.enum(JOURNEY_TRIGGERS),
  triggerEvent: Slug.nullable().optional(),
  triggerConditions: JourneyTriggerConditions.optional(),
  segmentId: Uuid.nullable().optional(),
  exitEvents: z.array(Slug).max(20).optional(),
  reentry: z.enum(["once", "after_days"]).optional(),
  reentryAfterDays: z.number().int().min(1).max(3650).nullable().optional(),
  steps: JourneySteps,
};

export const JourneyCreateInput = z.object({ ...JourneyBody, code: Slug }).strict();
export type JourneyCreateInput = z.infer<typeof JourneyCreateInput>;

export const JourneyUpdateInput = z.object({ ...JourneyBody, name: Name.optional(), channel: MessagingChannel.optional(), triggerKind: z.enum(JOURNEY_TRIGGERS).optional(), steps: JourneySteps.optional() }).strict();
export type JourneyUpdateInput = z.infer<typeof JourneyUpdateInput>;

export const JourneyEnrollInput = z.object({ contactId: Uuid, rehearsal: z.boolean().default(false) }).strict();
export type JourneyEnrollInput = z.infer<typeof JourneyEnrollInput>;

/** People added by hand at one go: the screen's selection, and a size a person can still read the answer of. */
export const JOURNEY_MANUAL_BATCH_MAX = 50;
export const JourneyManualInput = z.object({ contactIds: z.array(Uuid).min(1).max(JOURNEY_MANUAL_BATCH_MAX) }).strict();
export type JourneyManualInput = z.infer<typeof JourneyManualInput>;

export const JourneyStatusInput = z.object({ status: z.enum(JOURNEY_STATUSES) }).strict();

// ---------------------------------------------------------------------------
// Host actions
// ---------------------------------------------------------------------------

/** The data of the `action.result` event a host posts back after an `action.requested` webhook. */
export const HostActionResultData = z.object({ requestId: Uuid, ok: z.boolean(), result: z.record(z.string(), z.unknown()).default({}), error: z.string().max(500).optional() }).strict();
export type HostActionResultData = z.infer<typeof HostActionResultData>;

export const HostActionRequestInput = z.object({ action: Slug, contactId: Uuid.optional(), params: z.record(z.string(), z.unknown()).default({}), timeoutHours: z.number().int().min(1).max(720).default(48) }).strict();
export type HostActionRequestInput = z.infer<typeof HostActionRequestInput>;

// ---------------------------------------------------------------------------
// Referral
// ---------------------------------------------------------------------------

export const REFERRAL_REWARDS = ["voucher", "stamp", "free_days"] as const;
export const ReferralProgramInput = z
  .object({
    status: z.enum(["active", "paused"]).default("active"),
    rewardKind: z.enum(REFERRAL_REWARDS),
    rewardValue: z.string().trim().min(1).max(100),
    rewardAction: Slug,
    qualifyingEvent: Slug,
    landingUrl: HttpsUrl,
    maxRewardsPerContact: z.number().int().min(1).max(1000).nullable().optional(),
  })
  .strict();
export type ReferralProgramInput = z.infer<typeof ReferralProgramInput>;

// ---------------------------------------------------------------------------
// Settings, domains, direct send, DSAR
// ---------------------------------------------------------------------------

export const ChannelSettingsInputDto = z
  .object({
    enabledChannels: z.array(MessagingChannel).optional(),
    maxEmailsPerWeek: z.number().int().min(0).max(1000).optional(),
    maxSmsPerWeek: z.number().int().min(0).max(1000).optional(),
    maxPerContactPerDay: z.number().int().min(0).max(100).nullable().optional(),
    maxPerDay: z.number().int().min(0).max(10_000_000).nullable().optional(),
    quietHoursStart: z.number().int().min(0).max(24).optional(),
    quietHoursEnd: z.number().int().min(0).max(24).optional(),
    doubleOptIn: z.boolean().optional(),
    senderBrand: z.string().trim().max(200).nullable().optional(),
    senderLegalName: z.string().trim().max(200).nullable().optional(),
    senderAddress: z.string().trim().max(500).nullable().optional(),
    senderRegistration: z.string().trim().max(100).nullable().optional(),
    senderContactEmail: z.string().trim().max(320).nullable().optional(),
    fromName: z.string().trim().max(100).nullable().optional(),
    fromAddress: z.string().trim().max(320).nullable().optional(),
    smsSenderId: z.string().trim().max(20).nullable().optional(),
    viberSenderId: z.string().trim().max(50).nullable().optional(),
    whatsappSenderId: z.string().trim().max(50).nullable().optional(),
    blacklistDomains: z.array(z.string().trim().min(1).max(253)).max(1000).optional(),
    blacklistPhones: z.array(z.string().trim().min(1).max(32)).max(1000).optional(),
    defaultLanguage: MessagingLanguage.nullable().optional(),
    languages: z.array(MessagingLanguage).max(4).optional(),
  })
  .strict();
export type ChannelSettingsInputDto = z.infer<typeof ChannelSettingsInputDto>;

export const SuppressionAddInput = z.object({ family: z.enum(["email", "phone"]), address: z.string().trim().min(3).max(320), reason: z.enum(["admin", "blacklist"]).default("admin") }).strict();
export type SuppressionAddInput = z.infer<typeof SuppressionAddInput>;

export const SuppressionRemoveInput = z.object({ family: z.enum(["email", "phone"]), address: z.string().trim().min(3).max(320) }).strict();

export const SendingDomainInput = z.object({ domain: z.string().trim().toLowerCase().regex(/^(?!-)[a-z0-9-]{1,63}(\.[a-z0-9-]{1,63})+$/) }).strict();
export type SendingDomainInput = z.infer<typeof SendingDomainInput>;

export const DirectSendInput = z
  .object({
    contactId: Uuid,
    channel: MessagingChannel,
    /** The host's idempotency key: the same key twice is one message. */
    idempotencyKey: z.string().trim().min(8).max(200),
    subject: Subject.nullable().optional(),
    html: Html.nullable().optional(),
    text: Body.nullable().optional(),
    whatsappTemplate: z.string().trim().max(200).nullable().optional(),
    locale: MessagingLanguage.nullable().optional(),
    /** "service" is always refused; the field exists so a host can't leave the question unanswered by accident. */
    category: z.enum(["marketing", "service"]).default("marketing"),
  })
  .strict();
export type DirectSendInput = z.infer<typeof DirectSendInput>;

export const DsarRequestInput = z.object({ contactId: Uuid }).strict();

// ---------------------------------------------------------------------------
// What the API answers. Responses pass through these, so claim tokens and other internals never leave.
// ---------------------------------------------------------------------------

const When = IsoDateTime;

export const MessageTemplateDto = z.object({
  id: Uuid,
  name: z.string(),
  channel: z.string(),
  language: z.string(),
  subject: z.string().nullable(),
  html: z.string().nullable(),
  text: z.string().nullable(),
  whatsappTemplate: z.string().nullable(),
  variables: z.array(z.string()),
  fallbacks: z.unknown(),
  status: z.string(),
  match: z.unknown(),
  origin: z.string(),
  factIds: z.array(z.string()),
  checks: z.unknown(),
  version: z.number().int(),
  createdByName: z.string(),
  createdAt: When,
  updatedAt: When,
});
export type MessageTemplateDto = z.infer<typeof MessageTemplateDto>;
export const MessageTemplateList = z.object({ templates: z.array(MessageTemplateDto) });

export const MessageTemplatePreviewDto = z.object({
  /** The name in the mailbox's From column. */
  fromName: z.string().nullable().optional(),
  subject: z.string().nullable(),
  html: z.string().nullable(),
  text: z.string(),
  footerMissing: z.boolean(),
  frames: z.object({ desktop: z.object({ width: z.number() }), phone: z.object({ width: z.number() }) }),
  inbox: z.unknown(),
  darkMode: z.string(),
  sms: z.unknown(),
  missing: z.array(z.string()),
  checks: z.unknown(),
});

export const TemplateTestSendResult = z.object({ status: z.enum(["sent", "dry_run", "blocked"]), providerId: z.string().nullable().optional(), reason: z.string().optional(), missing: z.array(z.string()).optional(), detail: z.string().optional() });

export const CampaignDto = z.object({
  id: Uuid,
  name: z.string(),
  channel: z.string(),
  segmentId: z.string().nullable(),
  templateId: z.string().nullable(),
  language: z.string().nullable(),
  subject: z.string().nullable(),
  subjectB: z.string().nullable(),
  html: z.string().nullable(),
  text: z.string().nullable(),
  whatsappTemplate: z.string().nullable(),
  values: z.unknown(),
  abTestPercent: z.number().int().nullable(),
  abWinnerRule: z.string().nullable(),
  abWaitHours: z.number().int(),
  abWinner: z.string().nullable(),
  abDecidedAt: When.nullable(),
  status: z.string(),
  rehearsal: z.boolean(),
  scheduledAt: When.nullable(),
  recurrence: z.string().nullable(),
  runNo: z.number().int(),
  maxPerDay: z.number().int().nullable(),
  audienceCount: z.number().int(),
  lastError: z.string().nullable(),
  complianceReport: z.unknown(),
  startedAt: When.nullable(),
  completedAt: When.nullable(),
  createdByName: z.string(),
  createdAt: When,
  updatedAt: When,
});
export type CampaignDto = z.infer<typeof CampaignDto>;
export const CampaignList = z.object({ campaigns: z.array(CampaignDto) });

export const CampaignReportDto = z.object({ status: z.enum(["ok", "blocked"]), problems: z.array(z.object({ code: z.string(), detail: z.string().optional() })), compliance: z.unknown(), boundary: z.object({ transactional: z.boolean(), topic: z.string().nullable() }), at: z.string() });

export const CampaignStatsDto = z.object({
  runNo: z.number().int(),
  recipients: z.number().int(),
  pending: z.number().int(),
  byOutcome: z.record(z.string(), z.number()),
  byReason: z.record(z.string(), z.number()),
  variants: z.array(z.object({ variant: z.string(), sent: z.number(), opened: z.number(), clicked: z.number() })),
});

export const JourneyDto = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  status: z.string(),
  packDefault: z.string().nullable(),
  triggerKind: z.string(),
  triggerEvent: z.string().nullable(),
  triggerConditions: z.unknown(),
  segmentId: z.string().nullable(),
  channel: z.string().nullable(),
  currentVersion: z.number().int(),
  exitEvents: z.array(z.string()),
  reentry: z.string(),
  reentryAfterDays: z.number().int().nullable(),
  createdByName: z.string(),
  createdAt: When,
  updatedAt: When,
  steps: z.array(z.unknown()).optional(),
});
export type JourneyDto = z.infer<typeof JourneyDto>;
export const JourneyList = z.object({ journeys: z.array(JourneyDto) });

export const JourneyEnrollmentDto = z.object({
  id: Uuid,
  journeyId: Uuid,
  version: z.number().int(),
  contactId: Uuid,
  status: z.string(),
  stepIndex: z.number().int(),
  nextRunAt: When.nullable(),
  heldSince: When.nullable(),
  heldReason: z.string().nullable(),
  rehearsal: z.boolean(),
  exitReason: z.string().nullable(),
  enrolledAt: When,
  completedAt: When.nullable(),
});
export const JourneyEnrollmentList = z.object({ enrollments: z.array(JourneyEnrollmentDto) });
export const JourneyEnrollResult = z.object({ enrolled: z.boolean(), reason: z.string().optional(), enrollment: JourneyEnrollmentDto.optional() });
/** `channel` is the journey's first message, the one the check was made for; null when it sends nothing. `reason` null means they can be added. */
export const JourneyManualCheckDto = z.object({ channel: z.string().nullable(), items: z.array(z.object({ contactId: Uuid, reason: z.string().nullable() })) });
export type JourneyManualCheckDto = z.infer<typeof JourneyManualCheckDto>;
export const JourneyManualEnrollDto = z.object({
  enrolled: z.number().int(),
  refused: z.number().int(),
  items: z.array(z.object({ contactId: Uuid, enrolled: z.boolean(), enrollmentId: Uuid.optional(), reason: z.string().optional() })),
});
export type JourneyManualEnrollDto = z.infer<typeof JourneyManualEnrollDto>;
export const JourneyStepRunDto = z.object({
  stepIndex: z.number().int(),
  kind: z.string(),
  outcome: z.string(),
  detail: z.string().nullable(),
  dryRun: z.boolean(),
  at: When,
  message: z.object({ channel: z.string(), status: z.string(), subject: z.string().nullable(), preview: z.string().nullable() }).nullable(),
});
export const JourneyStepRunList = z.object({ steps: z.array(JourneyStepRunDto) });
export const JourneyStatsDto = z.object({ byStatus: z.record(z.string(), z.number()), held: z.number().int(), bySteps: z.array(z.object({ step: z.number(), kind: z.string(), outcome: z.string(), count: z.number() })) });
export const JourneyDefaultsResult = z.object({ created: z.array(z.string()) });

export const HostActionDto = z.object({
  id: Uuid,
  action: z.string(),
  params: z.unknown(),
  sourceKind: z.string(),
  sourceId: z.string(),
  contactId: z.string().nullable(),
  status: z.string(),
  result: z.unknown(),
  error: z.string().nullable(),
  requestedAt: When,
  expiresAt: When,
  respondedAt: When.nullable(),
});
export const HostActionList = z.object({ actions: z.array(HostActionDto) });

export const ReferralProgramDto = z.object({ id: Uuid, status: z.string(), rewardKind: z.string(), rewardValue: z.unknown(), rewardAction: z.string(), qualifyingEvent: z.string(), landingUrl: z.string(), maxRewardsPerContact: z.number().int().nullable(), createdAt: When, updatedAt: When });
export const ReferralProgramAnswer = z.object({ program: ReferralProgramDto.nullable() });
export const ReferralStatsDto = z.object({ codes: z.number().int(), clicks: z.number().int(), registered: z.number().int(), qualified: z.number().int(), rewarded: z.number().int(), rewardFailed: z.number().int() });
export const ReferralCodeDto = z.object({ id: Uuid, contactId: Uuid, code: z.string(), clicks: z.number().int(), status: z.string(), link: z.string() });
export const ReferralCodeInput = z.object({ contactId: Uuid }).strict();

export const CountrySettingsDto = z.object({
  country: z.string(),
  enabledChannels: z.array(z.string()),
  maxEmailsPerWeek: z.number().int(),
  maxSmsPerWeek: z.number().int(),
  maxPerContactPerDay: z.number().int().nullable(),
  maxPerDay: z.number().int().nullable(),
  quietHoursStart: z.number().int(),
  quietHoursEnd: z.number().int(),
  doubleOptIn: z.boolean(),
  sender: z.unknown(),
  blacklistDomains: z.array(z.string()),
  blacklistPhones: z.array(z.string()),
  defaultLanguage: z.string().nullable(),
  languages: z.array(z.string()),
});
/** What the owner sees where they send: why e-mail stopped or goes out gradually, and how much has gone. */
export const EmailStandingDto = z.object({
  live: z.boolean(),
  state: z.enum(["ok", "warning", "stopped"]),
  reason: z.enum(["hard_bounces", "complaints"]).nullable(),
  stoppedAt: IsoDateTime.nullable(),
  window: z.object({
    days: z.number().int(),
    minSent: z.number().int(),
    sent: z.number().int(),
    hardBounces: z.number().int(),
    complaints: z.number().int(),
    hardBounceRate: z.number(),
    complaintRate: z.number(),
  }),
  limits: z.object({ hardBounceRate: z.number(), complaintRate: z.number() }),
  warmup: z.object({
    /** Null when none applies: past the warm-up, or sending from the tenant's own verified domain. */
    ceiling: z.number().int().nullable(),
    sentToday: z.number().int(),
    ownDomain: z.boolean(),
  }),
});
export type EmailStandingDto = z.infer<typeof EmailStandingDto>;

export const MessagingOverviewDto = z.object({
  live: z.boolean(),
  countries: z.array(CountrySettingsDto),
  tenantLanguages: z.array(z.string()),
  availableLanguages: z.array(z.string()),
  activeJourneys: z.array(z.object({ id: Uuid, code: z.string(), name: z.string(), channel: z.string().nullable() })),
  suppressions: z.record(z.string(), z.number()),
  dryRunMessages: z.number().int(),
});
export const SuppressionDto = z.object({ id: Uuid, family: z.string(), addressMasked: z.string(), reason: z.string(), source: z.string(), createdBy: z.string().nullable(), createdAt: When });
export const SuppressionList = z.object({ suppressions: z.array(SuppressionDto), nextCursor: Uuid.nullable() });
export const SuppressionAdded = z.object({ created: z.boolean() });
/** `q` is one whole address or phone: the table keeps a keyed hash of each, so nothing shorter can be matched. */
export const SuppressionListQuery = z.object({ family: z.enum(["email", "phone"]).optional(), reason: z.string().max(40).optional(), q: z.string().trim().min(1).max(320).optional(), cursor: Uuid.optional(), limit: z.coerce.number().int().min(1).max(200).optional() });
export const DryRunListQuery = z.object({ cursor: Uuid.optional(), limit: z.coerce.number().int().min(1).max(200).optional(), sourceKind: z.string().max(40).optional(), sourceId: z.string().max(200).optional() });
export const DryRunMessageDto = z.object({ id: Uuid, contactId: Uuid, channel: z.string(), sourceKind: z.string(), sourceId: z.string(), status: z.string(), subject: z.string().nullable(), preview: z.string().nullable(), createdAt: When });
export const DryRunMessageList = z.object({ messages: z.array(DryRunMessageDto) });

export const SendingDomainDto = z.object({ id: Uuid, domain: z.string(), kind: z.string(), status: z.string(), dkimStatus: z.string().nullable(), spfStatus: z.string().nullable(), records: z.unknown(), lastCheckedAt: When.nullable(), createdAt: When });
export const SendingDomainList = z.object({ domains: z.array(SendingDomainDto), serviceDomain: z.string() });

export const DeliveryOutcomeDto = z.object({ status: z.enum(["sent", "dry_run", "skipped", "failed"]), messageId: z.string().nullable(), providerId: z.string().nullable().optional(), reason: z.string().optional(), detail: z.string().optional(), error: z.string().optional() });

export const DsarRequestDto = z.object({ id: Uuid, contactId: Uuid, kind: z.string(), status: z.string(), result: z.unknown(), error: z.string().nullable(), requestedBy: z.string(), requestedAt: When, completedAt: When.nullable() });
export const DsarRequestList = z.object({ requests: z.array(DsarRequestDto) });
export const DsarExportDto = z.object({ requestId: Uuid, exportedAt: z.string(), people: z.array(z.object({ contactId: Uuid, domains: z.record(z.string(), z.unknown()) })) });
export const DsarListQuery = z.object({ contactId: Uuid.optional() });

/** One grant, with the proof it was given. `formName` and `wording` are there only when a form of ours collected it. */
export const ConsentRecordDto = z.object({
  id: Uuid,
  family: z.string(),
  source: z.string(),
  textVersion: z.string(),
  formName: z.string().nullable(),
  wordingVersion: z.number().int().nullable(),
  wordingLanguage: z.string().nullable(),
  wording: z.string().nullable(),
  state: z.enum(["pending", "granted", "withdrawn"]),
  doubleOptIn: z.string(),
  proofAt: When,
  proofIp: z.string().nullable(),
  proofUserAgent: z.string().nullable(),
  proofHostRef: z.string().nullable(),
  confirmedAt: When.nullable(),
  withdrawnAt: When.nullable(),
  withdrawnSource: z.string().nullable(),
});
export const ConsentPersonDto = z.object({
  contactId: Uuid,
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.string(),
  emailSuppressed: z.boolean(),
  phoneSuppressed: z.boolean(),
  records: z.array(ConsentRecordDto),
});
export type ConsentPersonDto = z.infer<typeof ConsentPersonDto>;
export const ConsentPeopleQuery = z.object({
  q: z.string().trim().min(2).max(200).optional(),
  contactId: Uuid.optional(),
  /** People with at least one grant in this state. */
  state: z.enum(["pending", "granted", "withdrawn"]).optional(),
  cursor: Uuid.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type ConsentPeopleQuery = z.infer<typeof ConsentPeopleQuery>;
export const ConsentPeoplePage = z.object({ items: z.array(ConsentPersonDto), nextCursor: Uuid.nullable() });
export type ConsentPeoplePage = z.infer<typeof ConsentPeoplePage>;

export const WebhookAck = z.object({ ok: z.literal(true), applied: z.number().int().optional(), ignored: z.number().int().optional() });

/** The host answering an action it was asked to do: this isn't a conversion, so it gets its own answer. */
export const ActionResultAccepted = z.object({ duplicate: z.boolean(), action: z.object({ status: z.string(), applied: z.boolean() }) });
